import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { renderBold, stripBold } from "../lib/markdown";

const ContentContext = createContext({ content: {}, ready: false });

export function ContentProvider({ children }) {
    const [content, setContent] = useState({});
    const [ready, setReady] = useState(false);
    useEffect(() => {
        // Cache-bust with a timestamp so browsers always fetch fresh CMS text.
        api.get(`/content?t=${Date.now()}`, { headers: { "Cache-Control": "no-cache" } })
            .then((r) => {
                // /content returns { key: value } object. Preserve empty strings so consumers
                // can distinguish "user cleared this field" ("") from "never saved" (undefined).
                const raw = r.data || {};
                const map = {};
                if (Array.isArray(raw)) {
                    raw.forEach((c) => { if (c.value !== undefined && c.value !== null) map[c.key] = c.value; });
                } else if (typeof raw === "object") {
                    Object.entries(raw).forEach(([k, v]) => { if (v !== undefined && v !== null) map[k] = v; });
                }
                setContent(map);
            })
            .catch(() => {})
            .finally(() => setReady(true));
    }, []);

    const value = useMemo(() => ({ content, ready }), [content, ready]);
    return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

/**
 * Returns a t(key, fallback) function that renders **bold** markdown as React nodes.
 * Safe: uses React children (never dangerouslySetInnerHTML). Any user HTML stays escaped.
 * If you need a plain string (e.g. for alt="", title="" or string operations), use useTRaw().
 */
export function useT() {
    const { content } = useContext(ContentContext);
    return useCallback(
        (k, fallback = "") => {
            const val = content[k] && content[k].length ? content[k] : fallback;
            return renderBold(val);
        },
        [content],
    );
}

/** Same as useT but returns the raw string (with **markers** stripped) — for props/string ops. */
export function useTRaw() {
    const { content } = useContext(ContentContext);
    return useCallback(
        (k, fallback = "") => stripBold(content[k] && content[k].length ? content[k] : fallback),
        [content],
    );
}

/**
 * Returns an image URL for a CMS-managed key. If the stored value is a full URL,
 * returns it as-is; if it's a file id, wraps with /api/files/<id>; otherwise falls back.
 */
export function useImg() {
    const { content } = useContext(ContentContext);
    return useCallback((k, fallback = "") => {
        const v = content[k];
        if (!v) return fallback;
        if (/^https?:\/\//.test(v)) return v;
        return fileUrl(v);
    }, [content]);
}

export function useContent() { return useContext(ContentContext); }
